/**
 * A page's uncommitted burst, stored synchronously when the page may be unloading (flashcards-editor-home.decisions.md,
 * "Writing a page"). The commit buffer writes a burst only after it goes idle, the commit itself is asynchronous work in
 * the workspace worker, and the worker refuses the page's work after pagehide, so a page closed inside that window kept
 * nothing. localStorage is the one synchronous store a hidden page can still write; the next open of the workspace
 * commits what is stored here. Same practice: Chrome Page Lifecycle ("save state on visibilitychange hidden"),
 * Excalidraw's localStorage save on unload.
 *
 * Each stored burst is an immutable record under its own key: a commit, or a replay, removes exactly the records it
 * captured, so a record written meanwhile (a newer burst of the same page, another tab's) always survives.
 */
/** Only the CRDT body frame: it merges idempotently. A pending title is not recovered (flashcards-editor-home.decisions.md,
 * "Writing a page": scalar title writes have no revision that holds across replicas). */
export type PageUnloadEntry = { pageId: string; frame: Uint8Array }
export type PageUnloadScope = { userId: string; workspaceId: string }

const prefix = 'page-unload-journal'

/** Throws when storage refuses the write (quota, blocked site data): the caller reports it. Returns the record's key. */
export function writePageUnload(storage: Storage, scope: PageUnloadScope, entry: PageUnloadEntry) {
  const key = JSON.stringify([prefix, scope.userId, scope.workspaceId, entry.pageId, crypto.randomUUID()])
  storage.setItem(key, JSON.stringify({ ...entry, frame: entry.frame.toBase64() }))
  return key
}

/** This user's stored records for one workspace. `read` parses and validates one record and throws on a malformed one. */
export function listPageUnloads(storage: Storage, userId: string, workspaceId: string) {
  return _keys(storage, userId).filter(stored => stored.workspaceId === workspaceId).map(({ key: stored }) => ({ key: stored, read: () => _parse(storage.getItem(stored)) }))
}

/** Sign-out: the signed-out user's records leave the device. */
export function purgePageUnloads(storage: Storage, userId: string) {
  for (const { key: stored } of _keys(storage, userId)) storage.removeItem(stored)
}

function _keys(storage: Storage, userId: string) {
  const keys: { key: string; workspaceId: unknown }[] = []
  for (let index = 0; index < storage.length; index++) {
    const stored = storage.key(index)
    if (!stored?.startsWith(`["${prefix}",`)) continue
    // A key this module did not write is skipped; its value is never read.
    let parts: unknown
    try { parts = JSON.parse(stored) } catch { continue }
    if (Array.isArray(parts) && parts[1] === userId) keys.push({ key: stored, workspaceId: parts[2] })
  }
  return keys
}

function _parse(value: string | null): PageUnloadEntry {
  const entry = JSON.parse(value ?? 'null') as Record<string, unknown> | null
  if (!entry || typeof entry.pageId !== 'string' || typeof entry.frame !== 'string') throw new Error('Malformed page unload entry')
  return { pageId: entry.pageId, frame: Uint8Array.fromBase64(entry.frame) }
}
